import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  AuthFlowType,
  ChallengeNameType,
} from '@aws-sdk/client-cognito-identity-provider';
import { config } from '../config';

const STORAGE_KEY = 'andmore.auth.tokens';
const REFRESH_BUFFER_MS = 60_000;

const client = new CognitoIdentityProviderClient({ region: config.cognito.region });

export interface UserInfo {
  email: string;
  sub: string;
  emailVerified: boolean;
}

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface JwtPayload {
  sub: string;
  email?: string;
  email_verified?: boolean | string;
  exp: number;
}

function decodeJwt(token: string): JwtPayload {
  const [, payload] = token.split('.');
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(json);
}

function loadTokens(): StoredTokens | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function saveTokens(t: StoredTokens) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

function clearTokens() {
  localStorage.removeItem(STORAGE_KEY);
}

// Strong random password used when a user signs up in OTP-only mode.
// They never see or use it directly — sign-in goes through Cognito's
// USER_AUTH + PASSWORD challenge with this stashed value, which avoids
// the "two emails on first sign-in" wart of EMAIL_OTP-after-confirm.
// 20 chars, mixed case + digit + symbol — satisfies AWS default policy.
function generateRandomPassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const symbols = '!@#$%^&*()-_=+[]{}<>';
  const charset = upper + lower + digits + symbols;
  const required = [pickFrom(upper), pickFrom(lower), pickFrom(digits), pickFrom(symbols)];
  const filler = Array.from({ length: 16 }, () => pickFrom(charset));
  return shuffle([...required, ...filler]).join('');
}

function pickFrom(set: string): string {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return set[buf[0] % set.length];
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const j = buf[0] % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function persistFromAuthResult(
  refreshTokenFallback: string,
  result: { IdToken?: string; AccessToken?: string; RefreshToken?: string; ExpiresIn?: number },
) {
  if (!result.IdToken || !result.AccessToken) {
    throw new Error('Cognito returned no tokens');
  }
  const expiresAt = Date.now() + (result.ExpiresIn ?? 3600) * 1000;
  saveTokens({
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken ?? refreshTokenFallback,
    expiresAt,
  });
}

export interface SignUpResult {
  // True if the pool auto-confirmed the user (e.g. via PreSignUp trigger).
  // False means Cognito sent an email-verification code and the UI must
  // collect it via confirmSignUp(...) before the user can sign in.
  userConfirmed: boolean;
  // Password to use when signing in. Same as the one passed in for the
  // password mode; randomly generated for OTP-only mode. Callers should
  // hold onto this between signUp() and the post-confirm sign-in step.
  effectivePassword: string;
}

export class AuthService {
  // Creates a Cognito user. Pass `password` when the user is opting into
  // password sign-in; omit it for OTP-only sign-up (we generate a strong
  // random password under the hood). Returns whether Cognito auto-confirmed
  // the user — if not, the UI must collect the email code via confirmSignUp.
  async signUp(email: string, password?: string): Promise<SignUpResult> {
    const effectivePassword = password ?? generateRandomPassword();
    const res = await client.send(
      new SignUpCommand({
        ClientId: config.cognito.clientId,
        Username: email,
        Password: effectivePassword,
        UserAttributes: [{ Name: 'email', Value: email }],
      }),
    );
    return { userConfirmed: res.UserConfirmed === true, effectivePassword };
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    await client.send(
      new ConfirmSignUpCommand({
        ClientId: config.cognito.clientId,
        Username: email,
        ConfirmationCode: code,
      }),
    );
  }

  async resendSignUpCode(email: string): Promise<void> {
    await client.send(
      new ResendConfirmationCodeCommand({
        ClientId: config.cognito.clientId,
        Username: email,
      }),
    );
  }

  // Step 1 of OTP sign-in. Returns the Session token to pass into completeOtpSignIn.
  async startOtpSignIn(email: string): Promise<string> {
    const res = await client.send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_AUTH,
        ClientId: config.cognito.clientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: ChallengeNameType.EMAIL_OTP,
        },
      }),
    );
    if (res.ChallengeName !== ChallengeNameType.EMAIL_OTP || !res.Session) {
      throw new Error('Unexpected Cognito response — could not start OTP sign-in');
    }
    return res.Session;
  }

  // Step 2 of OTP sign-in. On success, tokens are persisted.
  async completeOtpSignIn(email: string, session: string, code: string): Promise<void> {
    const res = await client.send(
      new RespondToAuthChallengeCommand({
        ChallengeName: ChallengeNameType.EMAIL_OTP,
        ClientId: config.cognito.clientId,
        Session: session,
        ChallengeResponses: {
          USERNAME: email,
          EMAIL_OTP_CODE: code,
        },
      }),
    );
    if (!res.AuthenticationResult) {
      throw new Error('Cognito did not return tokens for the OTP code');
    }
    persistFromAuthResult('', res.AuthenticationResult);
  }

  async signInWithPassword(email: string, password: string): Promise<void> {
    // USER_AUTH with PREFERRED_CHALLENGE=PASSWORD expects the password value
    // in AuthParameters up front so Cognito can complete auth in a single
    // round-trip. Omitting it produces "Missing required parameter PASSWORD".
    const init = await client.send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_AUTH,
        ClientId: config.cognito.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
          PREFERRED_CHALLENGE: ChallengeNameType.PASSWORD,
        },
      }),
    );
    // Common case: Cognito returns tokens immediately.
    if (init.AuthenticationResult) {
      persistFromAuthResult('', init.AuthenticationResult);
      return;
    }
    // Fallback: pool returns a PASSWORD challenge — respond with the password.
    if (init.ChallengeName === ChallengeNameType.PASSWORD && init.Session) {
      const res = await client.send(
        new RespondToAuthChallengeCommand({
          ChallengeName: ChallengeNameType.PASSWORD,
          ClientId: config.cognito.clientId,
          Session: init.Session,
          ChallengeResponses: {
            USERNAME: email,
            PASSWORD: password,
          },
        }),
      );
      if (!res.AuthenticationResult) {
        throw new Error('Cognito did not return tokens for the password');
      }
      persistFromAuthResult('', res.AuthenticationResult);
      return;
    }
    throw new Error(
      `Unexpected Cognito response — expected tokens or PASSWORD challenge, got ${init.ChallengeName ?? 'no challenge'}`,
    );
  }

  signOut(): void {
    clearTokens();
  }

  async getIdToken(): Promise<string | null> {
    const tokens = loadTokens();
    if (!tokens) return null;
    if (tokens.expiresAt - Date.now() > REFRESH_BUFFER_MS) {
      return tokens.idToken;
    }
    try {
      const refreshed = await this.refresh(tokens.refreshToken);
      return refreshed.idToken;
    } catch {
      clearTokens();
      return null;
    }
  }

  async getUserInfo(): Promise<UserInfo | null> {
    const token = await this.getIdToken();
    if (!token) return null;
    const payload = decodeJwt(token);
    return {
      email: payload.email ?? '',
      sub: payload.sub,
      emailVerified: payload.email_verified === true || payload.email_verified === 'true',
    };
  }

  async isAuthenticated(): Promise<boolean> {
    return (await this.getIdToken()) !== null;
  }

  private async refresh(refreshToken: string): Promise<StoredTokens> {
    const res = await client.send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: config.cognito.clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
    );
    if (!res.AuthenticationResult) {
      throw new Error('Refresh failed');
    }
    persistFromAuthResult(refreshToken, res.AuthenticationResult);
    return loadTokens()!;
  }
}

export const authService = new AuthService();
